/** @format */
"use client";
import Link from "next/link";
import { useState, useCallback } from "react";
import { CategoryAPI } from "@/api/categories/category.api";
import { CategoryInterface } from "@/types/category";
import { ProductWidgets } from "@/components/ui/product/product";
import { useParams } from "next/navigation";
import {
  ApiResponseProductScroll,
  ScrollToLoadProductsWithCategory,
} from "@/components/scrollToLoad/scrollToLoadProductWithCategoryComponent";
import { ProductInterface } from "@/types/product";
import ServicesSection from "./components/ServicesSection";
import ProjectsSection from "./components/ProjectsSection";
import NewsSection from "./components/NewsSection";

export enum CategoryVariant {
  NEWS = "NEWS",
  COURSES = "COURSES",
  SERVICES = "SERVICES",
  PROMOTION = "PROMOTION",
  CONTACT = "CONTACT",
  PROJECTS = "PROJECTS",
}

interface Props {
  categoryProps: CategoryInterface;
}

const DanhMucPageClient = ({ categoryProps }: Props) => {
  const { slug } = useParams();
  const [loading] = useState(false);
  const [error] = useState<string | null>(null);
  const category: CategoryInterface = categoryProps;

  // ✅ Wrap with useCallback to maintain stable reference
  const fetchProductsFromCategory = useCallback(
    async (page: number, limit: number): Promise<ApiResponseProductScroll> => {
      try {
        const res = await CategoryAPI.getCategoryWithSlug(
          slug?.toString() || "",
          page,
          limit,
          false,
        );

        const categoryData = res.data;
        const products = categoryData.products;

        return {
          products: products ?? [],
          hasMore: categoryData.products?.length === limit,
          nextPage:
            categoryData.products?.length === limit ? page + 1 : undefined,
          total: categoryData.totalProducts,
        };
      } catch (err) {
        console.error("Error fetching products:", err);
        return {
          products: [],
          hasMore: false,
          nextPage: undefined,
          total: 0,
        };
      }
    },
    [slug], // ✅ Only recreate when slug changes
  );

  // ✅ Wrap with useCallback for stable reference
  const handleProductsLoaded = useCallback(
    (newProducts: ProductInterface[], totalLoaded: number) => {
      console.log(
        `Loaded ${newProducts.length} new products. Total: ${totalLoaded}`,
      );
    },
    [],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/30">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <ProductWidgets.cardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="container mx-auto px-4 py-16 min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-8 max-w-md mx-auto">
          <div className="relative">
            <div className="w-32 h-32 mx-auto bg-[var(--gradient-accent)] rounded-full flex items-center justify-center shadow-[var(--shadow-default)] animate-pulse">
              <svg
                className="w-16 h-16 text-[var(--color-primary)]"
                fill="currentColor"
                viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c1.19 0 2.34-.21 3.41-.6.3-.11.49-.4.49-.72 0-.43-.35-.78-.78-.78-.25 0-.47.12-.61.3-.85.29-1.76.44-2.71.44-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8c0 .95-.15 1.86-.44 2.71-.18.14-.3.36-.3.61 0 .43.35.78.78.78.32 0 .61-.19.72-.49.39-1.07.6-2.22.6-3.41 0-5.52-4.48-10-10-10zm0 6c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z" />
                <path d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                <rect
                  x="13"
                  y="3"
                  width="1"
                  height="4"
                  transform="rotate(15 13.5 5)"
                />
                <rect
                  x="15"
                  y="4"
                  width="1"
                  height="4"
                  transform="rotate(15 15.5 6)"
                />
                <rect
                  x="17"
                  y="5"
                  width="1"
                  height="4"
                  transform="rotate(15 17.5 7)"
                />
                <rect
                  x="19"
                  y="6"
                  width="1"
                  height="4"
                  transform="rotate(15 19.5 8)"
                />
                <rect
                  x="21"
                  y="7"
                  width="1"
                  height="4"
                  transform="rotate(15 21.5 9)"
                />
              </svg>
            </div>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-[var(--color-primary)] rounded-full flex items-center justify-center animate-bounce">
              <span className="text-xs text-[var(--color-text-white)]">♪</span>
            </div>
            <div
              className="absolute -bottom-1 -left-3 w-5 h-5 bg-[var(--color-accent-green)] rounded-full flex items-center justify-center animate-bounce"
              style={{ animationDelay: "0.5s" }}>
              <span className="text-xs text-[var(--color-text-white)]">♫</span>
            </div>
          </div>

          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-[var(--color-text-primary)]">
              Không Tìm Thấy Danh Mục Guitar
            </h1>
            <div className="space-y-2">
              <p className="text-[var(--color-text-secondary)] text-lg leading-relaxed">
                Danh mục guitar bạn đang tìm kiếm không tồn tại hoặc đã bị di
                chuyển.
              </p>
              <p className="text-[var(--color-text-muted)] text-sm">
                Có thể danh mục này đã được cập nhật hoặc thay đổi tên.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Link
              href="/"
              className="inline-flex items-center gap-3 bg-[var(--gradient-primary)] text-[var(--color-text-white)] px-8 py-4 rounded-xl hover:shadow-[var(--shadow-hover)] transition-all duration-300 font-semibold text-lg group">
              <svg
                className="w-5 h-5 group-hover:scale-110 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              Về Trang Chủ
              <div className="w-2 h-2 bg-white/30 rounded-full group-hover:translate-x-1 transition-transform duration-300"></div>
            </Link>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                href="/danh-muc"
                className="inline-flex items-center gap-2 bg-[var(--color-bg-accent)] text-[var(--color-text-primary)] px-6 py-3 rounded-lg hover:bg-[var(--color-bg-hover)] transition-all duration-300 font-medium border border-[var(--color-border)] group">
                <svg
                  className="w-4 h-4 group-hover:scale-110 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                Xem Tất Cả Danh Mục
              </Link>

              <Link
                href="/lien-he"
                className="inline-flex items-center gap-2 bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] px-6 py-3 rounded-lg hover:bg-[var(--color-bg-accent)] transition-all duration-300 font-medium border border-[var(--color-border-light)] group">
                <svg
                  className="w-4 h-4 group-hover:scale-110 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                Liên Hệ Hỗ Trợ
              </Link>
            </div>
          </div>

          <div className="bg-[var(--gradient-secondary)] border border-[var(--color-border-light)] rounded-xl p-6 text-left">
            <h3 className="text-[var(--color-text-primary)] font-semibold mb-3 flex items-center gap-2">
              <svg
                className="w-4 h-4 text-[var(--color-primary)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Gợi Ý Hữu Ích
            </h3>
            <ul className="space-y-2 text-sm text-[var(--color-text-secondary)]">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-[var(--color-primary)] rounded-full mt-2 flex-shrink-0"></div>
                Kiểm tra lại đường dẫn URL có chính xác không
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-[var(--color-primary)] rounded-full mt-2 flex-shrink-0"></div>
                Thử tìm kiếm sản phẩm guitar mong muốn
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-[var(--color-primary)] rounded-full mt-2 flex-shrink-0"></div>
                Liên hệ với chúng tôi để được hỗ trợ trực tiếp
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const renderVariantContent = () => {
    switch (category?.variant) {
      case CategoryVariant.NEWS:
        return <NewsSection />;
      case CategoryVariant.SERVICES:
        return <ServicesSection />;
      case CategoryVariant.PROJECTS:
        return <ProjectsSection />;
      default:
        return <div>Variant not supported</div>;
    }
  };

  return (
    <div className="min-h-screen">
      <div className="py-2">
        {category.variant ? (
          <div className="space-y-8">{renderVariantContent()}</div>
        ) : (
          <div className="space-y-8 container mx-auto">
            <ScrollToLoadProductsWithCategory
              categoryName={category.name}
              fetchProducts={fetchProductsFromCategory}
              itemsPerPage={8}
              containerClassName="mb-8"
              loadOffset={150}
              onProductsLoaded={handleProductsLoaded}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DanhMucPageClient;
